import { ArrowRight, ArrowUpRight, Bot, Files, Globe2, Users } from "lucide-react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { useState } from "react";
import { Link } from "react-router-dom";

const PATHS = [
  {
    icon: Files,
    problem: "A operação depende de planilhas.",
    direction: "Sistema sob medida",
    title: "Transforme regras espalhadas em uma operação central.",
    text: "Criamos uma plataforma ao redor do seu processo, reunindo dados, responsáveis, aprovações e indicadores em um único ambiente.",
    outcomes: ["Uma fonte de verdade", "Responsabilidades claras", "Visão em tempo real"],
    accent: "#d9e021",
  },
  {
    icon: Users,
    problem: "Leads e clientes se perdem no caminho.",
    direction: "CRM e vendas",
    title: "Faça cada oportunidade ter um próximo passo.",
    text: "Organizamos contatos, conversas e negociações para a equipe saber o que aconteceu, quem é responsável e qual ação vem agora.",
    outcomes: ["Pipeline visível", "Histórico centralizado", "Follow-up consistente"],
    accent: "#00d2ff",
  },
  {
    icon: Bot,
    problem: "A equipe repete tarefas todos os dias.",
    direction: "Automação e IA",
    title: "Deixe pessoas cuidarem do que exige decisão.",
    text: "Conectamos sistemas, mensagens e inteligência artificial para executar rotinas, organizar informações e acelerar respostas.",
    outcomes: ["Menos trabalho manual", "Respostas mais rápidas", "Fluxos sempre ativos"],
    accent: "#f3b63f",
  },
  {
    icon: Globe2,
    problem: "O digital não representa o nível da empresa.",
    direction: "Sites e experiências web",
    title: "Transforme presença digital em percepção de valor.",
    text: "Desenhamos uma experiência clara, rápida e visualmente precisa para apresentar a empresa e conduzir cada visitante à ação certa.",
    outcomes: ["Mensagem mais clara", "Experiência memorável", "Mais conversas qualificadas"],
    accent: "#9fb8ff",
  },
];

export const ServiceIndex = () => {
  const [active, setActive] = useState(0);
  const reduce = useReducedMotion();
  const selected = PATHS[active];

  return (
    <section id="diagnostico" data-header-theme="dark" className="overflow-hidden bg-[#050606] py-24 text-white md:py-36" data-testid="services-index">
      <div className="site-shell">
        <div className="grid gap-8 border-b border-white/12 pb-10 lg:grid-cols-[.72fr_1.28fr] lg:items-end lg:pb-14">
          <p className="eyebrow">Por onde começar</p>
          <h2 className="display-title max-w-5xl text-4xl leading-[1.02] text-[#f1f3ec] sm:text-5xl lg:text-7xl">
            Comece pelo atrito que mais custa hoje.
          </h2>
        </div>

        <div className="grid lg:grid-cols-[.9fr_1.1fr]">
          <div className="border-b border-white/12 lg:border-b-0 lg:border-r">
            {PATHS.map(({ icon: Icon, problem }, index) => {
              const isActive = index === active;
              return (
                <button
                  key={problem}
                  type="button"
                  onClick={() => setActive(index)}
                  onMouseEnter={() => setActive(index)}
                  className={`group relative grid w-full grid-cols-[44px_minmax(0,1fr)] items-center gap-4 border-b border-white/12 py-5 pr-8 text-left transition-colors last:border-b-0 sm:grid-cols-[52px_minmax(0,1fr)_auto] sm:py-8 sm:pr-4 lg:pr-8 ${isActive ? "text-white" : "text-white/38 hover:text-white/72"}`}
                  aria-pressed={isActive}
                >
                  {isActive && <motion.span layoutId="diagnostic-active" className="absolute inset-y-0 left-0 w-[3px]" style={{ backgroundColor: selected.accent }} transition={{ type: "spring", stiffness: 320, damping: 30 }} />}
                  <span className={`flex h-10 w-10 items-center justify-center border transition-colors sm:h-11 sm:w-11 ${isActive ? "border-white/28 bg-white/[.06]" : "border-white/10"}`}><Icon size={18} /></span>
                  <span className="display-title min-w-0 text-lg leading-tight sm:text-2xl lg:text-3xl">{problem}</span>
                  <ArrowRight size={18} className={`absolute right-1 transition-transform sm:static ${isActive ? "translate-x-0 opacity-100" : "-translate-x-2 opacity-0 group-hover:translate-x-0 group-hover:opacity-70"}`} />
                </button>
              );
            })}
          </div>

          <div className="relative min-h-[500px] overflow-hidden lg:min-h-[620px]">
            <div className="diagnostic-grid absolute inset-0 opacity-60" aria-hidden="true" />
            <div className="absolute inset-y-0 left-0 w-px bg-white/10 lg:hidden" />
            <AnimatePresence mode="wait" initial={false}>
              <motion.div
                key={active}
                initial={reduce ? false : { opacity: 0, x: 36 }}
                animate={{ opacity: 1, x: 0 }}
                exit={reduce ? undefined : { opacity: 0, x: -24 }}
                transition={{ duration: 0.42, ease: [0.16, 1, 0.3, 1] }}
                className="relative flex min-h-[500px] flex-col justify-between p-7 sm:p-10 lg:min-h-[620px] lg:p-14"
              >
                <div>
                  <div className="flex items-center justify-between gap-6 border-b border-white/12 pb-5">
                    <p className="text-[9px] font-bold uppercase tracking-[.18em] text-white/32">Direção recomendada</p>
                    <span className="h-2 w-2 rounded-full shadow-[0_0_18px_currentColor]" style={{ color: selected.accent, backgroundColor: selected.accent }} />
                  </div>
                  <p className="mt-8 text-xs font-bold uppercase tracking-[.16em]" style={{ color: selected.accent }}>{selected.direction}</p>
                  <h3 className="display-title mt-5 max-w-2xl text-3xl leading-[1.04] sm:text-4xl lg:text-6xl">{selected.title}</h3>
                  <p className="mt-7 max-w-xl text-sm leading-relaxed text-white/52 sm:text-base">{selected.text}</p>
                </div>

                <div className="mt-12">
                  <div className="grid border-y border-white/12 sm:grid-cols-3">
                    {selected.outcomes.map((outcome, index) => (
                      <div key={outcome} className={`py-4 text-xs font-semibold text-white/70 sm:px-4 sm:py-5 ${index > 0 ? "border-t border-white/12 sm:border-l sm:border-t-0" : ""}`}>{outcome}</div>
                    ))}
                  </div>
                  <Link to="/contato" className="group mt-8 inline-flex min-h-14 items-center gap-4 rounded-full bg-[#f1f3ec] py-2 pl-6 pr-2 text-sm font-bold text-[#050606] transition-colors hover:bg-lime-300">
                    Conversar sobre esse desafio
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#050606] text-white"><ArrowUpRight size={16} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" /></span>
                  </Link>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
};
